import * as React from 'react'
import classNames from 'classnames'

import { NewsfeedEntry } from 'core/interfaces'
import { Text, TextName } from 'sdk/text/Text'
import { NewsfeedBigCard } from 'pages/newsfeed/components/newsfeed-big-card/NewsfeedBigCard'
import { NewsfeedCard } from 'pages/newsfeed/components/newsfeed-card/NewsfeedCard'
import { Button } from 'sdk/button/Button'

import classes from './NewsSectionGrid.module.scss'

interface ComponentProps {
  title: string
  entries: NewsfeedEntry[]
  enableLoadMore?: boolean
  initialVisibleCount?: number
  itemsPerPage?: number
}

export const NewsSectionGrid: React.FC<ComponentProps> = ({
  title,
  entries,
  enableLoadMore = false,
  initialVisibleCount = 3,
  itemsPerPage = 3,
}) => {
  const [visibleCount, setVisibleCount] = React.useState(initialVisibleCount)
  const [lastRevealStart, setLastRevealStart] = React.useState<number | null>(null)

  const visibleEntries = enableLoadMore ? entries.slice(0, visibleCount) : entries.slice(0, 4)
  const hasMore = enableLoadMore && visibleCount < entries.length
  const bigCardEntry = visibleEntries[0]
  const regularCardEntries = visibleEntries.slice(1)

  const handleLoadMore = (): void => {
    setLastRevealStart(visibleCount)
    setVisibleCount((previousCount) => Math.min(previousCount + itemsPerPage, entries.length))
  }

  const shouldAnimate = (index: number): boolean => (
    lastRevealStart !== null && index >= lastRevealStart
  )

  if (entries.length === 0) {
    return null
  }

  return (
    <div className={classes.section}>
      <Text type={TextName.H2} className={classes.sectionTitle}>
        {title}
      </Text>

      {bigCardEntry && (
        <div
          className={classNames(
            classes.bigCardContainer,
            classes.cardItem,
            { [classes.cardItemAnimated]: shouldAnimate(0) },
          )}
        >
          <NewsfeedBigCard entry={bigCardEntry} showLearnMore />
        </div>
      )}

      {regularCardEntries.length > 0 && (
        <div className={classes.cardsGrid}>
          {regularCardEntries.map((entry, index) => {
            const entryIndex = index + 1
            return (
              <div
                key={entry.id ?? entryIndex}
                className={classNames(
                  classes.cardItem,
                  { [classes.cardItemAnimated]: shouldAnimate(entryIndex) },
                )}
              >
                <NewsfeedCard entry={entry} />
              </div>
            )
          })}
        </div>
      )}
      {hasMore ? (
        <div className={classes.loadMoreContainer}>
          <Button as="button" onClick={handleLoadMore} className={classes.loadMoreButton}>
            Load more
          </Button>
        </div>
      ) : null}
    </div>
  )
}
